
import { motion } from 'framer-motion';
import { OnSaleProduct } from '@/hooks/useOnSaleProducts';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { generateProductUrl } from '@/utils/productUrlUtils';
import { useState } from 'react';

interface SaleProductCardProps {
  product: OnSaleProduct & { short_description?: string | null };
}

const SaleProductCard = ({ product }: SaleProductCardProps) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const discount = product.compare_at_price && product.price
    ? Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100)
    : 0;

  const handleImageError = () => {
    setImageError(true);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  return (
    <Link to={generateProductUrl(product)} className="block group h-full">
      <motion.div
        className="relative bg-card/40 backdrop-blur-sm border border-border/50 rounded-xl overflow-hidden shadow-sm hover:shadow-md hover:border-primary/30 transition-all duration-300 h-full flex flex-col"
        whileHover={{ y: -4, scale: 1.01 }}
        transition={{ duration: 0.2 }}
      >
        {/* Image Container */}
        <div className="relative aspect-square overflow-hidden bg-muted/30">
          {!imageLoaded && !imageError && (
            <div className="absolute inset-0 bg-muted/50 animate-pulse" />
          )}
          
          {imageError ? (
            <div className="w-full h-full flex items-center justify-center bg-muted/50">
              <div className="text-center text-muted-foreground">
                <div className="w-16 h-16 mx-auto mb-2 bg-muted-foreground/10 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">📷</span>
                </div>
                <p className="text-xs">No image</p>
              </div>
            </div>
          ) : (
            <img
              src={product.image_url || '/placeholder.svg'}
              alt={product.name}
              className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
              onError={handleImageError}
              onLoad={handleImageLoad}
              loading="lazy"
            />
          )}
          
          {discount > 0 && (
            <Badge
              variant="destructive"
              className="absolute top-3 right-3 text-xs font-bold shadow-lg"
            >
              -{discount}%
            </Badge>
          )}
        </div>

        {/* Content Container */}
        <div className="p-4 flex-grow flex flex-col">
          <h3 className="font-playfair text-lg font-bold text-foreground line-clamp-2 leading-tight mb-2 min-h-[3.5rem]">
            {product.name}
          </h3>
          
          {/* Price Section */}
          <div className="mt-auto space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-2xl font-bold text-foreground">
                ${product.price?.toFixed(2)}
              </span>
              {product.compare_at_price && (
                <span className="text-base text-muted-foreground line-through">
                  ${product.compare_at_price.toFixed(2)}
                </span>
              )}
            </div>
            
            {/* Short Description */}
            {product.short_description && (
              <motion.p 
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.3 }}
                className="text-sm text-muted-foreground/90 line-clamp-2 leading-relaxed pt-1"
              >
                {product.short_description}
              </motion.p>
            )}
          </div>
        </div>
      </motion.div>
    </Link>
  );
};

export default SaleProductCard;
